/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable } from "@angular/core";
import { Capacitor } from "@capacitor/core";
import { InAppPurchaseService } from "@malirang/api-client";
import {
    LOG_LEVEL,
    LogInResult,
    MakePurchaseResult,
    PACKAGE_TYPE,
    Purchases,
    PurchasesOfferings,
    PurchasesPackage,
} from "@revenuecat/purchases-capacitor";
import { environment } from "../../env/env";

@Injectable({
    providedIn: "root",
})
export class IapService {
    private readonly inAppPurchaseService = inject(InAppPurchaseService);

    // MARK: - 초기화
    public async initializePurchases(): Promise<void> {
        // 웹 플랫폼이나 네이티브가 아닌 경우 초기화하지 않음
        if (
            typeof window === "undefined" ||
            Capacitor.getPlatform() === "web" ||
            !Capacitor.isNativePlatform()
        )
            return;

        // 로그 레벨 설정 (에러만 로깅)
        await Purchases.setLogLevel({ level: LOG_LEVEL.ERROR });

        const platform = Capacitor.getPlatform();
        // 환경 설정 파일에서 플랫폼에 맞는 Public API Key 로드
        const apiKey =
            platform === "ios" ? environment.iap.ios : environment.iap.android;

        try {
            // RevenueCat SDK Public Key를 사용하여 초기화
            await Purchases.configure({ apiKey });
            console.log(
                `RevenueCat이 성공적으로 초기화되었습니다. (${platform})`,
            );
            // 초기화 후 오퍼링 데이터 로드 시도
            await this.getOfferings();
        } catch (error) {
            console.error("RevenueCat 초기화 실패:", error);
        }
    }

    // MARK: - 사용자 식별 및 로그인
    public async identifyUser(
        userId: string,
    ): Promise<LogInResult | undefined> {
        // 웹 플랫폼이 아닌 네이티브 환경에서만 실행
        if (Capacitor.getPlatform() === "web" || !Capacitor.isNativePlatform())
            return;

        try {
            // 사용자 ID로 RevenueCat에 로그인/식별 (UUID를 추가하여 고유성 확보)
            const result = await Purchases.logIn({
                appUserID: `${userId}`,
            });
            console.log(
                "사용자 식별 성공 (Original App User ID):",
                result.customerInfo.originalAppUserId,
            );
            return result;
        } catch (error) {
            console.error("사용자 식별 실패:", error);
            throw error;
        }
    }

    // MARK: - 오퍼링 조회 (상품 목록 조회)
    public async getOfferings(): Promise<PurchasesOfferings["current"] | null> {
        if (Capacitor.getPlatform() === "web" || !Capacitor.isNativePlatform())
            return null;

        try {
            const offerings = await Purchases.getOfferings();
            const current = offerings.current;
            const count = current?.availablePackages.length ?? 0;
            console.log(`오퍼링 로드 완료: ${count}개의 패키지`);
            return current;
        } catch (error) {
            console.error("오퍼링 로드 실패:", error);
            throw error;
        }
    }

    // MARK: - LIFETIME 구매 (Webhook 기반, Receipt Validation 생략)
    public async getOfferingsAndPurchase(): Promise<MakePurchaseResult | null> {
        console.log("=== LIFETIME 구매 프로세스 시작 ===");

        try {
            const current = await this.getOfferings();
            if (!current) {
                console.warn("구매 가능한 오퍼링이 없습니다.");
                return null;
            }

            // LIFETIME 타입 패키지 찾기
            const lifetimePackage = current.availablePackages.find(
                (pkg: PurchasesPackage) =>
                    pkg.packageType === PACKAGE_TYPE.LIFETIME,
            );

            if (!lifetimePackage) {
                console.warn("LIFETIME 패키지를 찾을 수 없습니다.");
                return null;
            }

            console.log("✅ 패키지 구매 시작:", lifetimePackage.identifier);

            // RevenueCat SDK를 통한 구매 시도
            const purchaseResult: MakePurchaseResult =
                await Purchases.purchasePackage({
                    aPackage: lifetimePackage,
                });

            console.log("✅ 구매 완료!");

            const transaction = purchaseResult.transaction;
            const productId = transaction?.productIdentifier || "";
            const transactionId = transaction?.transactionIdentifier || "";

            console.log("=== 구매 완료 ===", {
                productId,
                transactionId,
                info: "서버 Webhook이 자동으로 구매 정보를 수신합니다.",
            });

            return purchaseResult;
        } catch (error: any) {
            console.error("❌ 구매 실패:", error.message || error);
            throw error;
        }
    }
}
